import _ from "lodash";
/** external */
import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
/** internal */
import {
  encodeBlanksContent,
  encodeBlanksContentDocEditor,
} from "Worksheet/modules/WorksheetUtils";
import { getIsWorksheetDocEditorEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import {
  fillInTheBlanksModes,
  BLANKS_MIN_WIDTH,
  BLANKS_MAX_WIDTH,
} from "Worksheet/modules/WorksheetConstants";
import TextViewWrapper from "Worksheet/components/TextViewWrapper";
import QuestionFieldComponent from "../QuestionFieldComponent";
/** relative */
import classes from "./FillInTheBlanks.scss";

class FillInTheBlanks extends PureComponent {
  constructor(props) {
    super(props);

    if (props.customRef) {
      props.customRef(this);
    }

    this.contentRef = null;
    this.showDocEditor = getIsWorksheetDocEditorEnabled();
    this.inputHandler = e => this.onInputChange({ e });
  }

  componentDidMount() {
    if (!this.showDocEditor) {
      this.updateInputValues();
      this.contentRef?.addEventListener("input", this.inputHandler);
    }
  }

  componentWillUnmount() {
    if (!this.showDocEditor) {
      this.contentRef?.removeEventListener("input", this.inputHandler);
    }
  }

  componentDidUpdate(prevProps) {
    const { mode } = this.props;

    if (!_.isEqual(prevProps?.mode, mode) && !this.showDocEditor) {
      this.updateInputValues();
    }
  }

  updateInputValues = () => {
    const responses = _.get(this.props, "value.responses", {});

    this.contentRef?.querySelectorAll?.("#blanks-input")?.forEach(input => {
      if (input) {
        const existingValue = _.get(responses, _.get(input, "name", ""), "");
        if (!_.isEmpty(existingValue)) {
          input.setAttribute("value", existingValue);
        }

        input.style.width = `${this.getInputWidth(input)}px`;
      }
    });
  };

  setContainerRef = ref => {
    this.contentRef = ref;
  };

  getInputWidth = targetElement => {
    /** Auto grow text field as input value changes */
    /** reference: https://github.com/h5p/h5p-blanks/blob/2b9a8612d0cc3812b8931fa953f034b6aa8ebb77/js/blanks.js#L418 */
    const fontSize = window?.getComputedStyle(targetElement)?.fontSize;
    const minPadding = 0.5 * parseInt(fontSize, 10);
    const value = _.get(targetElement, "value");

    /** make a temporary div to compute the content's pixel width */
    const tempDiv = document.createElement("div");
    tempDiv.textContent = value;
    tempDiv.style.position = "absolute";
    tempDiv.style.whiteSpace = "nowrap";
    tempDiv.style.fontSize = fontSize;
    tempDiv.style.fontFamily = window?.getComputedStyle(
      targetElement
    )?.fontFamily;
    tempDiv.style.padding = window?.getComputedStyle(targetElement)?.padding;
    tempDiv.style.width = "initial";
    targetElement.parentNode?.appendChild(tempDiv);
    const width = tempDiv?.offsetWidth;
    tempDiv?.remove();

    const inputWidth = _.min([
      _.max([BLANKS_MIN_WIDTH, minPadding + width]),
      BLANKS_MAX_WIDTH,
    ]);

    return inputWidth;
  };

  onInputChange = ({ e = {}, val = {} }) => {
    const oldResponses = _.get(this.props, "value.responses", {});

    const { mode, updateInputField, fieldKey } = this.props;

    const targetElement = _.get(e, "target");
    const value = _.get(targetElement, "value");

    const updatedResponses = {
      ...oldResponses,
      ...(!_.isEmpty(val) && _.isObject(val)
        ? val
        : { [_.get(targetElement, "name")]: value }),
    };

    if (targetElement) {
      targetElement.style.width = `${this.getInputWidth(targetElement)}px`;
    }

    updateInputField({
      params: {
        [fieldKey]: updatedResponses,
      },
      isDebounce: true,
      isMutation: !_.isEqual(mode, "create"),
    });
  };

  isValid = () => {
    const { value, customValidation } = this.props;

    const customError = customValidation(_.get(value, "fullValue", ""));
    if (customError != "") {
      return 1;
    }

    return 0;
  };

  render() {
    const {
      value,
      mode,
      emptyText,
      t,
      isAiGeneratedField,
      worksheetMode,
      hideBorder,
      fieldKey,
    } = this.props;

    const { question = "", answers = [], responses = {} } = value || {};

    const containerWrapperClass = classNames({
      [classes.content]: true,
      [classes.preview]: mode === "preview",
    });

    if (!this.showDocEditor) {
      let encodedData = encodeBlanksContent({
        question,
        answers,
        mode,
        responses,
        t,
        isAiGeneratedField,
      });
      // To prevent the attribute from being stripped during the sanitization process when using getStyleStrippedText
      encodedData = _.replace(encodedData, /viewBox/g, "viewbox");
      return isAiGeneratedField ? (
        <TextViewWrapper
          value={encodedData}
          isAiGeneratedField={isAiGeneratedField}
        />
      ) : (
        <div className={containerWrapperClass} ref={this.setContainerRef}>
          <QuestionFieldComponent
            type={"WorksheetTextView"}
            fieldValue={encodedData || emptyText}
            fieldMode={"view"}
            sanitizationType={"SANITIZE_DANGER_HTML_PARAGRAPH"}
            mode={worksheetMode}
            hideBorder={hideBorder}
            fieldUid={fieldKey}
          />
        </div>
      );
    }

    const encodedData = encodeBlanksContentDocEditor({
      question,
      answers,
      mode,
      responses,
      t,
      isAiGeneratedField,
      onInputChange: this.onInputChange,
    });

    return (
      <div className={containerWrapperClass} ref={this.setContainerRef}>
        <QuestionFieldComponent
          type={"WorksheetTextView"}
          fieldValue={_.get(encodedData, "html", emptyText)}
          fieldMode={"view"}
          sanitizationType={"SANITIZE_DANGER_HTML_PARAGRAPH"}
          mode={worksheetMode}
          hideBorder={hideBorder}
          fieldUid={fieldKey}
          fieldMetaData={_.get(encodedData, "blanksMeta", {})}
        />
      </div>
    );
  }
}

FillInTheBlanks.propTypes = {
  value: PropTypes.object,
  mode: PropTypes.oneOf(fillInTheBlanksModes),
  updateInputField: PropTypes.func,
  fieldKey: PropTypes.string,
  t: PropTypes.func,
  isAiGeneratedField: PropTypes.bool,
};

FillInTheBlanks.defaultProps = {
  mode: "view",
  isAiGeneratedField: false,
};

export default FillInTheBlanks;
